import type { FC, ReactNode } from 'react'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { FeedbackDialog } from '@/components/generals/feedback-dialog'
import { siteConfig } from '@/constants/site'

const sections: Array<{ id: string, title: string, body: ReactNode }> = [
    {
        id: 'short-version',
        title: 'The short version',
        body: 'You do not need an account to use CaveDL, and we do not sell your data. We only handle what is needed to get your download ready.'
    },
    {
        id: 'links',
        title: 'Links you paste',
        body: 'When you press Download, the link you pasted is sent to the CaveDL download server, which fetches the file and hands it back to your browser. The file is saved on your own device.'
    },
    {
        id: 'statistics',
        title: 'Visitor statistics',
        body: 'We count visits with privacy-friendly statistics that do not use cookies and do not identify you. We see things like which pages are viewed, the country, browser and type of device, never who you are.'
    },
    {
        id: 'cookies',
        title: 'Cookies',
        body: 'The CaveDL web app does not use cookies for tracking or advertising.'
    },
    {
        id: 'feedback',
        title: 'Feedback',
        body: 'If you send us feedback, we store your message, and your email address if you choose to give it, so we can read it and reply. You can ask us to delete it at any time.'
    },
    {
        id: 'telegram',
        title: 'The Telegram bot and payments',
        body: 'The CaveDL bot runs inside Telegram, which has its own privacy policy. Bot Pro payments are handled inside Telegram, and we never see your card details.'
    }
]

const Privacy: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo
                title='Privacy policy'
                description='How CaveDL handles the links you paste, visitor statistics, feedback and Telegram payments, in plain language.'
            />

            <article className='mx-auto max-w-3xl px-4 py-16 sm:px-6 sm:py-20'>
                <header id='privacy' className='border-b border-border pb-8'>
                    <p className='text-sm font-semibold tracking-wide text-primary'>
                        Privacy policy
                    </p>

                    <h1 className='mt-3 font-heading text-4xl font-bold tracking-tight text-balance sm:text-5xl'>
                        Your privacy at CaveDL
                    </h1>

                    <p className='mt-4 text-lg leading-relaxed text-muted-foreground'>
                        Plain language, no fine print. Here is what happens when you use CaveDL.
                    </p>
                </header>

                <div className='flex flex-col gap-10 pt-10'>
                    {sections.map((section) => (
                        <section key={section.id} id={section.id} aria-labelledby={`${section.id}-title`}>
                            <h2 id={`${section.id}-title`} className='font-heading text-2xl font-bold tracking-tight'>
                                {section.title}
                            </h2>

                            <p className='mt-3 leading-relaxed text-muted-foreground'>
                                {section.body}
                            </p>
                        </section>
                    ))}

                    <section id='contact' aria-labelledby='contact-title' className='rounded-3xl bg-accent p-6 sm:p-8'>
                        <h2 id='contact-title' className='font-heading text-2xl font-bold tracking-tight text-accent-foreground'>
                            Questions?
                        </h2>

                        <p className='mt-3 leading-relaxed text-accent-foreground'>
                            <FeedbackDialog className='cursor-pointer font-semibold underline underline-offset-4'>
                                Send us a message
                            </FeedbackDialog>
                            {' '}
                            or reach us through the
                            {' '}
                            <a
                                href={siteConfig.links.telegramBot}
                                target='_blank'
                                rel='noopener noreferrer'
                                className='font-semibold underline underline-offset-4'
                            >
                                CaveDL bot on Telegram
                            </a>
                            .
                        </p>
                    </section>
                </div>
            </article>
        </Layout>
    )
}

export default Privacy
